import { Component, input, output } from '@angular/core';

/**
 * GlobalSettingsListComponent
 * Reusable shell for global-settings list pages (badges, categories, regions, causes, …).
 * Renders the shared chrome: header bar, toolbar (search + add), scrollable table area,
 * and footer total count. The table content is projected via <ng-content>.
 *
 * Usage:
 *   <app-global-settings-list
 *     [title]="..."
 *     [iconSrc]="..."          (optional)
 *     [backButtonLabel]="..."
 *     [searchPlaceholder]="..."
 *     [addButtonLabel]="..."
 *     [totalLabel]="..."
 *     [totalCount]="..."
 *     [isLoading]="..."
 *     [searchTerm]="..."
 *     (searchChanged)="..."
 *     (addNewClicked)="..."
 *     (backClicked)="...">
 *     <!-- projected table goes here -->
 *   </app-global-settings-list>
 */
@Component({
  selector: 'app-global-settings-list',
  standalone: false,
  templateUrl: './global-settings-list.component.html',
  styleUrl: './global-settings-list.component.scss',
})
export class GlobalSettingsListComponent {
  /** Page title shown in the header bar */
  title = input<string>('');

  /** Optional icon rendered in a red square in the header bar */
  iconSrc = input<string>('');

  /** Label for the back button */
  backButtonLabel = input<string>('');

  /** Placeholder text for the search input */
  searchPlaceholder = input<string>('');

  /** Label for the Add New button */
  addButtonLabel = input<string>('');

  /** Label shown before the total count in the footer */
  totalLabel = input<string>('');

  /** Total item count shown in the footer */
  totalCount = input<number>(0);

  /** Controls the loading state — shows a spinner when true */
  isLoading = input<boolean>(false);

  /** Set to false to hide the footer total count row */
  showFooter = input<boolean>(true);

  /** Current search term (controlled from parent) */
  searchTerm = input<string>('');

  /** Message shown when list is empty and no search is active */
  emptyStateLabel = input<string>('No items yet.');

  /** Message shown when search returns zero results */
  noResultsLabel = input<string>('No results found.');

  /** Emits the new search string on every keystroke */
  searchChanged = output<string>();

  /** Emits when the Add New button is clicked */
  addNewClicked = output<void>();

  /** Emits when the Back button is clicked */
  backClicked = output<void>();

  onSearchInput(event: Event): void {
    this.searchChanged.emit((event.target as HTMLInputElement).value);
  }

  onClearSearch(): void {
    this.searchChanged.emit('');
  }
}
